import React from 'react';
import {
  Box,
  Paper,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import { RichEditor } from '../../base/RichEditor';
import { Code as CodeIcon, Download as DownloadIcon } from '@mui/icons-material';

/**
 * RichEditor Test Page
 * Test component for TipTap RichEditor implementation
 */
const RichEditorTestPage: React.FC = () => {
  const [editorContent, setEditorContent] = React.useState<string>('');
  const [testName, setTestName] = React.useState<string>('Test Document');
  const [showHTML, setShowHTML] = React.useState<boolean>(false);

  const handleContentChange = (content: string) => {
    console.log('Editor content changed:', content);
    setEditorContent(content);
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(
      {
        name: testName,
        content: editorContent,
        createdAt: new Date().toISOString(),
      },
      null,
      2
    );
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${testName}.json`;
    link.click();
  };

  const handleClear = () => {
    setEditorContent('');
    setTestName('Test Document');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          🧪 RichEditor Test Component
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Test TipTap RichEditor with full feature support: Bold, Italic, Code, Code Blocks, Lists, Headings, etc.
        </Typography>
      </Box>

      {/* Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Test the editor below. Try creating lists with bullet points and ordered lists to verify they display correctly
        with markers. Test all formatting options.
      </Alert>

      <Grid container spacing={3}>
        {/* Main Editor Section */}
        <Grid item xs={12} lg={showHTML ? 6 : 12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                📝 Editor
              </Typography>

              {/* Test Name Input */}
              <TextField
                fullWidth
                label="Test Document Name"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />

              {/* Rich Editor Component */}
              <RichEditor
                value={editorContent}
                onChange={handleContentChange}
                label="Content"
                minHeight={500}
                placeholder="Start typing... Try creating lists, headings, bold text, code blocks, etc."
              />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CodeIcon />}
                  onClick={() => setShowHTML(!showHTML)}
                  size="small"
                >
                  {showHTML ? 'Hide HTML' : 'Show HTML'}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  size="small"
                >
                  Export JSON
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClear}
                  size="small"
                >
                  Clear
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* HTML Preview Section */}
        {showHTML && (
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  📄 HTML Output
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    border: '1px solid #ddd',
                  }}
                >
                  {editorContent || '(Empty)'}
                </Paper>

                <Typography variant="caption" sx={{ mt: 1, color: '#999' }}>
                  HTML will be displayed here. Copy it to test structural accuracy.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Information Card */}
      <Card sx={{ mt: 3, backgroundColor: '#f9f9f9' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            ✓ Features to Test:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
            <li>
              <strong>Text Formatting:</strong> Bold, Italic, Code, Code Blocks
            </li>
            <li>
              <strong>Headings:</strong> H1 and H2 (check visual hierarchy)
            </li>
            <li>
              <strong>Lists:</strong> Bullet list (•) and Ordered list (1., 2., 3., ...) with markers
            </li>
            <li>
              <strong>Undo/Redo:</strong> Test with Undo and Redo buttons
            </li>
            <li>
              <strong>Button Responsiveness:</strong> All toolbar buttons should work with single click
            </li>
            <li>
              <strong>Content Export:</strong> Export as JSON to verify HTML structure
            </li>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RichEditorTestPage;
